import { Component, Input, Output, EventEmitter, forwardRef, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { ClickOutsideDirective } from 'src/app/core/directives/click-outside.directive';

export interface MultiColumnOption {
  value: string;
  label: string;
  [key: string]: any;
}

export type SelectionMode = 'single' | 'multiple';

@Component({
  selector: 'megastore-admin-multi-column-select',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
    ClickOutsideDirective
  ],
  templateUrl: './multi-column-select.component.html',
  styleUrls: ['./multi-column-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiColumnSelectComponent),
      multi: true
    }
  ]
})
export class MultiColumnSelectComponent implements OnInit, ControlValueAccessor {
  private _options: MultiColumnOption[] = [];
  
  //handle option keys on basis of selectionConfig
  @Input() 
  set options(value: Array<any>) {
    this._options = value.map(option => {
      const mappedOption = {
        ...option,
        value: option[this.selectionConfig.value],
        label: option[this.selectionConfig.label]
      };
      return mappedOption;
    });
    this.filteredOptions = [...this._options];
    this.updateSelectedLabel();
    this.cdr.detectChanges();
  }
  
  get options(): MultiColumnOption[] {
    return this._options;
  }
  
  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() columns: number = 3;
  @Input() maxHeight: string = '300px';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() errorMessage: string = '';
  @Input() tooltipThreshold: number = 25;
  @Input() selectionMode: SelectionMode = 'single';

  @Output() selectionChange = new EventEmitter<string | string[]>();

  isOpen: boolean = false;
  selectedValue: string | string[] = '';
  selectedLabel: string = '';
  protected selectedValues: string[] = [];
  protected selectedLabels: string[] = [];
  protected searchText: string = '';
  protected filteredOptions: Array<{ value: string; label: string, [key: string]: any }> = [];

  private onChange: (value: string | string[]) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() selectionConfig: any = {value: 'value', label: 'label'};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.updateSelectedLabel();
  }

  // ControlValueAccessor implementation
  writeValue(value: string | string[]): void {
    if (this.selectionMode === 'multiple') {
      this.selectedValues = Array.isArray(value) ? value : value ? [value] : [];
      this.selectedValue = this.selectedValues;
    } else {
      this.selectedValue = Array.isArray(value) ? (value.length > 0 ? value[0] : '') : value;
      this.selectedValues = this.selectedValue ? [this.selectedValue] : [];
    }
    this.updateSelectedLabel();
  }

  registerOnChange(fn: (value: string | string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    
    this.isOpen = !this.isOpen;
    
    if (this.isOpen) {
      this.searchText = '';
      this.filteredOptions = [...this.options];
      this.onTouched();
    }
  }

  selectOption(option: MultiColumnOption, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.selectionMode === 'multiple') {
      this.toggleMultipleSelection(option);
    } else {
      this.selectSingleOption(option);
    }
  }

  private selectSingleOption(option: MultiColumnOption): void {
    this.selectedValue = option[this.selectionConfig.value];
    this.selectedLabel = option[this.selectionConfig.label];
    this.selectedValues = [option[this.selectionConfig.value]];
    this.selectedLabels = [option[this.selectionConfig.label]];

    this.isOpen = false;
    this.onChange(option[this.selectionConfig.value]);
    this.selectionChange.emit(option[this.selectionConfig.value]);
  }

  private toggleMultipleSelection(option: MultiColumnOption): void {
    const index = this.selectedValues.indexOf(option[this.selectionConfig.value]);
    
    if (index > -1) {
      // Remove option if already selected
      this.selectedValues.splice(index, 1);
      this.selectedLabels.splice(index, 1);
    } else {
      // Add option if not selected
      this.selectedValues.push(option[this.selectionConfig.value]);
      this.selectedLabels.push(option[this.selectionConfig.label]);
    }

    // Update the display value
    this.selectedValue = this.selectedValues;
    this.selectedLabel = this.selectedLabels.join(', ');
    
    this.onChange(this.selectedValues);
    this.selectionChange.emit(this.selectedValues);
  }

  isOptionSelected(option: MultiColumnOption): boolean {
    if (this.selectionMode === 'multiple') {
      return this.selectedValues.includes(option[this.selectionConfig.value]);
    }
    return option[this.selectionConfig.value] === this.selectedValue;
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.searchText = '';
    this.filteredOptions = [...this.options];
  }

  updateSelectedLabel(): void {
    if (!this.options || this.options.length === 0) {
      this.selectedLabel = '';
      this.selectedValues = [];
      return;
    }

    if (this.selectionMode === 'multiple') {
      if (this.selectedValues && this.selectedValues.length > 0) {
        const selectedOptions = this.options.filter(option => 
          this.selectedValues.includes(option[this.selectionConfig.value])
        );
        this.selectedLabels = selectedOptions.map(option => option[this.selectionConfig.label]);
        this.selectedLabel = this.selectedLabels.join(', ');
      } else {
        this.selectedLabel = '';
        this.selectedValues = [];
      }
    } else {
      if (this.selectedValue) {
        const selectedOption = this.options.find(option => option[this.selectionConfig.value] === this.selectedValue);
        this.selectedLabel = selectedOption ? selectedOption[this.selectionConfig.label] : '';
      } else {
        this.selectedLabel = '';
      }
    }
  }

  onInputClick(event: Event): void {
    event.stopPropagation();
    this.toggleDropdown();
  }

  onDocumentClick(): void {
    this.closeDropdown();
  }

  isTextTruncated(text: string): boolean {
    return !!text && text.length > this.tooltipThreshold;
  }

  get filteredOptionsForColumns(): Array<Array<{ value: string; label: string, [key: string]: any }>> {
    const optionsToUse = this.searchText ? this.filteredOptions : this.options;
    const columns = this.columns || 1;
    const itemsPerColumn = Math.ceil(optionsToUse.length / columns);
    const result: Array<Array<{ value: string; label: string, [key: string]: any }>> = [];

    for (let i = 0; i < columns; i++) {
      const startIndex = i * itemsPerColumn;
      const endIndex = Math.min(startIndex + itemsPerColumn, optionsToUse.length);
      result.push(optionsToUse.slice(startIndex, endIndex));
    }

    return result;
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchText = target.value.toLowerCase();
    this.filterOptions();
  }

  private filterOptions(): void {
    if (!this.searchText.trim()) {
      this.filteredOptions = [...this.options];
    } else {
      this.filteredOptions = this.options.filter(option =>
        option[this.selectionConfig.label].toLowerCase().includes(this.searchText)
      );
    }
  }

  clearSearch(): void {
    this.searchText = '';
    this.filteredOptions = [...this.options];
  }


} 